"use client";
import { Logger } from "@shared/utils/logger";
import { useEffect, useRef, useCallback, useState } from "react";
import { LocalTrack, RemoteTrack } from "livekit-client";

/**
 * Return type for the useVideoPictureInPicture hook.
 * This is a fallback for browsers that don't support Document PiP (Firefox, Safari).
 */
export interface UseVideoPipReturn {
  /** Whether video element PiP is currently active */
  isPipActive: boolean;
  /** Whether the browser supports video element PiP */
  isPipSupported: boolean;
  /** Register a video element for PiP */
  registerVideoElement: (element: HTMLVideoElement | null) => void;
  /** Manually open video PiP */
  openPip: () => Promise<void>;
  /** Close video PiP */
  closePip: () => Promise<void>;
}

interface UseVideoPipOptions {
  /** Whether the feature is enabled */
  isActive?: boolean;
  /** Callback when PiP closes */
  onClose?: () => void;
  /** The track to use for PiP (priority: screen share > remote camera > local camera) */
  track?: LocalTrack | RemoteTrack | null;
}

/**
 * Check if Video Element PiP API is supported.
 * This is the older PiP API supported by Firefox, Safari, and all modern browsers.
 */
const isVideoPipSupported = (): boolean => {
  if (typeof document === "undefined") return false;
  // Check if document.pictureInPictureEnabled exists and is true
  return "pictureInPictureEnabled" in document && document.pictureInPictureEnabled === true;
};

/**
 * useVideoPictureInPicture - Fallback PiP using video element API.
 * Works in Firefox, Safari, and other browsers that don't support Document PiP.
 * Only shows a single video (the active speaker or screen share).
 */
export const useVideoPictureInPicture = ({ isActive = true, onClose }: UseVideoPipOptions = {}): UseVideoPipReturn => {
  const [isPipActive, setIsPipActive] = useState(false);
  const isPipSupported = isVideoPipSupported();

  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const isOpeningRef = useRef(false);
  const wasManuallyClosedRef = useRef(false);
  const pipOpenTimeRef = useRef<number>(0);

  /**
   * Register a video element for PiP.
   */
  const registerVideoElement = useCallback((element: HTMLVideoElement | null) => {
    setVideoElement(element);
  }, []);

  /**
   * Close video element PiP.
   */
  const closePip = useCallback(async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      }
      setIsPipActive(false);
      wasManuallyClosedRef.current = false;
      onClose?.();
    } catch (error) {
      Logger.warn("VideoPiP", "Failed to close PiP:", error);
    }
  }, [onClose]);

  /**
   * Open video element PiP.
   */
  const openPip = useCallback(async () => {
    if (!isPipSupported || !isActive || isOpeningRef.current) return;
    if (!videoElement) {
      Logger.warn("VideoPiP", "No video element registered for PiP");
      return;
    }

    // Don't reopen if already in PiP
    if (document.pictureInPictureElement === videoElement) return;

    isOpeningRef.current = true;

    try {
      // Check if video has enough data to play
      if (videoElement.readyState < 2) {
        Logger.warn("VideoPiP", "Video not ready for PiP");
        isOpeningRef.current = false;
        return;
      }

      await videoElement.requestPictureInPicture();
      setIsPipActive(true);
      wasManuallyClosedRef.current = false;
      pipOpenTimeRef.current = Date.now();
    } catch (error) {
      // Common errors: user gesture required, video not loaded
      Logger.debug("VideoPiP", "Could not open PiP:", error);
    } finally {
      isOpeningRef.current = false;
    }
  }, [isPipSupported, isActive, videoElement]);

  /**
   * Handle PiP events on the video element.
   */
  useEffect(() => {
    if (!isPipSupported) return;

    if (!isActive) {
      if (isPipActive) {
        closePip();
      }
      return;
    }

    const handleEnterPip = () => {
      setIsPipActive(true);
    };

    const handleLeavePip = () => {
      setIsPipActive(false);
      if (document.hidden) {
        wasManuallyClosedRef.current = true;
      }
      onClose?.();
    };

    const video = videoElement;
    if (video) {
      video.addEventListener("enterpictureinpicture", handleEnterPip);
      video.addEventListener("leavepictureinpicture", handleLeavePip);

      return () => {
        video.removeEventListener("enterpictureinpicture", handleEnterPip);
        video.removeEventListener("leavepictureinpicture", handleLeavePip);
      };
    }
  }, [isPipSupported, isActive, onClose, videoElement, isPipActive, closePip]);

  /**
   * Handle tab visibility change (Auto PiP).
   */
  useEffect(() => {
    if (!isPipSupported || !isActive) return;

    // Disable auto-PiP on mobile devices
    const isMobileScreen = typeof window !== "undefined" && window.innerWidth < 768;
    if (isMobileScreen) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (!wasManuallyClosedRef.current && !document.pictureInPictureElement) {
          openPip();
        }
      } else {
        closePip();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPipSupported, isActive, openPip, closePip]);

  /**
   * Cleanup on unmount.
   */
  useEffect(() => {
    return () => {
      closePip();
    };
  }, [closePip]);

  return {
    isPipActive,
    isPipSupported,
    registerVideoElement,
    openPip,
    closePip
  };
};
